import { useEffect, useRef } from 'react';
import { sendDriverLocation } from '@/domains/api/tracking.api';

interface UseDriverLocationSenderOptions {
  orderRequestId: number | null;
  enabled?: boolean;
  intervalMs?: number;
}

export function useDriverLocationSender({
  orderRequestId,
  enabled = true,
  intervalMs = 8000,
}: UseDriverLocationSenderOptions) {
  const watchIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || !orderRequestId || !navigator.geolocation) return;

    const sendPosition = (position: GeolocationPosition) => {
      sendDriverLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        order_request_id: orderRequestId,
        heading: position.coords.heading ?? undefined,
        speed: position.coords.speed ?? undefined,
      }).catch(() => {
        // Silently ignore transient network/GPS errors
      });
    };

    const handleError = () => {
      // Permission denied or unavailable — no-op
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      sendPosition,
      handleError,
      { enableHighAccuracy: true, maximumAge: intervalMs, timeout: 15000 },
    );

    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [enabled, orderRequestId, intervalMs]);
}
